/**
 * KOPS-528: Request Demo / Book Appointment Page
 * aisearchportal.com/request-demo
 * Navy + Gold design matching events page
 */

import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faRocket, faUsers, faChartLine, faLaptop } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF'
};

const RequestDemoPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '',
    industry_type: '', team_size: '', current_crm: '',
    preferred_time: '', message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Primary: create/update contact in CRM for automation pipeline
      await axiosInstance.post('/contacts/sync-single', {
        fullName: formData.name,
        email: formData.email,
        phone: formData.phone,
        brokerage: formData.company,
        source: 'demo_request'
      });

      // Secondary: log to demo_requests table for tracking
      try {
        await axiosInstance.post('/demo-requests/public/demo-request', formData);
      } catch (e) { /* demo_requests table is supplementary */ }

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again or text us at (520) 251-9595.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: colors.navy }}>
        <div style={{ background: colors.navy, padding: '60px 0', textAlign: 'center' }}>
          <Container>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#27ae60', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'white', fontSize: '40px' }} />
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', color: colors.white, letterSpacing: '2px' }}>
                Thank <span style={{ color: colors.gold2 }}>You!</span>
              </h2>
              <p style={{ color: colors.off, fontSize: '18px', marginBottom: '24px' }}>
                Check your email — your demo login credentials have been sent. You'll have instant access to explore the platform live.
              </p>
              <p style={{ color: colors.muted, fontSize: '14px', marginBottom: '32px' }}>
                Watch the video walkthrough first, then log in and explore at your own pace.
              </p>
              <div className="d-flex gap-3 justify-content-center">
                <Button href="https://demo.kairosiq.ai/agent-portal/login" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '12px 32px', color: colors.navy }}>
                  Launch Demo Now
                </Button>
                <Button href="/events" variant="outline-light" style={{ fontWeight: 600, padding: '12px 32px' }}>
                  View Events
                </Button>
              </div>
              <p style={{ color: colors.muted, fontSize: '13px', marginTop: '24px' }}>
                Questions? Text us anytime: <a href="sms:5202519595" style={{ color: colors.gold2 }}>(520) 251-9595</a>
              </p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.navy }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, padding: '40px 0 20px', textAlign: 'center' }}>
        <Container>
          <a href="/" style={{ textDecoration: 'none' }}>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '3px', color: colors.white, margin: 0 }}>
              KAIROS<span style={{ color: colors.gold2 }}>IQ</span>
            </h1>
            <p style={{ color: colors.muted, fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', margin: 0 }}>Intelligence at the Right Moment</p>
          </a>
        </Container>
      </div>

      {/* Hero */}
      <div style={{ background: colors.navy, padding: '40px 0' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, letterSpacing: '2px', marginBottom: '12px' }}>
                Book Your <span style={{ color: colors.gold2 }}>Free Demo</span>
              </h2>
              <p style={{ color: colors.off, fontSize: '18px', maxWidth: '600px', margin: '0 auto 16px' }}>
                See how AI-powered tools can transform your real estate business. Our support team will walk you through the platform and answer all your questions.
              </p>
              <p style={{ color: colors.muted, fontSize: '14px' }}>
                30-minute personalized walkthrough — no obligation, no pressure.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Value Props */}
      <div style={{ background: colors.navy2, padding: '30px 0' }}>
        <Container>
          <Row>
            {[
              { icon: faLaptop, text: 'AI Buyer Portal — your clients search with AI' },
              { icon: faChartLine, text: 'AI CMA Reports in 35 seconds' },
              { icon: faRocket, text: 'AI Lead Coach — knows who to call and what to say' },
              { icon: faUsers, text: 'CRM + MLS integration built for agents' },
            ].map((item, i) => (
              <Col sm={6} md={3} key={i} className="text-center" style={{ padding: '12px' }}>
                <FontAwesomeIcon icon={item.icon} style={{ color: colors.gold2, fontSize: '20px', marginBottom: '6px' }} />
                <p style={{ color: colors.off, fontSize: '13px', margin: 0 }}>{item.text}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Form */}
      <div style={{ background: colors.navy, padding: '40px 0 60px' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={7} lg={6}>
              <div style={{ background: colors.white, borderRadius: '12px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
                <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '1px', marginBottom: '4px', textAlign: 'center' }}>
                  Request Your <span style={{ color: colors.gold }}>Demo</span>
                </h3>
                <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', marginBottom: '24px' }}>
                  Fill out the form and our support team will contact you within 24 hours.
                </p>

                <Form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Control required name="name" value={formData.name} onChange={handleChange} placeholder="Full Name *" style={{ fontSize: '14px' }} />
                    </Col>
                    <Col md={6}>
                      <Form.Control required type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address *" style={{ fontSize: '14px' }} />
                    </Col>
                    <Col md={6}>
                      <Form.Control required name="company" value={formData.company} onChange={handleChange} placeholder="Brokerage / Company *" style={{ fontSize: '14px' }} />
                    </Col>
                    <Col md={6}>
                      <Form.Control required type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone Number *" style={{ fontSize: '14px' }} />
                    </Col>
                    <Col md={6}>
                      <Form.Select required name="industry_type" value={formData.industry_type} onChange={handleChange} style={{ fontSize: '14px', color: formData.industry_type ? '#333' : '#999' }}>
                        <option value="">Primary Focus *</option>
                        <option value="residential">Residential Real Estate</option>
                        <option value="commercial">Commercial Real Estate</option>
                        <option value="property_management">Property Management</option>
                        <option value="team_brokerage">Real Estate Team/Brokerage</option>
                        <option value="mortgage_lending">Mortgage / Lending</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Select required name="team_size" value={formData.team_size} onChange={handleChange} style={{ fontSize: '14px', color: formData.team_size ? '#333' : '#999' }}>
                        <option value="">Team Size *</option>
                        <option value="solo">Solo Agent (Just Me)</option>
                        <option value="small_team">2-5 Team Members</option>
                        <option value="medium_team">6-15 Team Members</option>
                        <option value="large_team">16-50 Team Members</option>
                        <option value="enterprise">50+ Team Members</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Select name="current_crm" value={formData.current_crm} onChange={handleChange} style={{ fontSize: '14px', color: formData.current_crm ? '#333' : '#999' }}>
                        <option value="">Current CRM (optional)</option>
                        <option value="none">No CRM Currently</option>
                        <option value="kvcore">kvCORE</option>
                        <option value="follow_up_boss">Follow Up Boss</option>
                        <option value="chime">Chime</option>
                        <option value="sierra">Sierra Interactive</option>
                        <option value="liondesk">LionDesk</option>
                        <option value="wise_agent">Wise Agent</option>
                        <option value="top_producer">Top Producer</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Select name="preferred_time" value={formData.preferred_time} onChange={handleChange} style={{ fontSize: '14px', color: formData.preferred_time ? '#333' : '#999' }}>
                        <option value="">Preferred Time (optional)</option>
                        <option value="morning">Morning (8am - 12pm)</option>
                        <option value="afternoon">Afternoon (12pm - 4pm)</option>
                        <option value="anytime">Anytime works</option>
                      </Form.Select>
                    </Col>
                    <Col md={12}>
                      <Form.Control as="textarea" rows={3} name="message" value={formData.message} onChange={handleChange}
                        placeholder="Tell us about your current challenges or questions (optional)"
                        style={{ fontSize: '14px', resize: 'vertical' }} />
                    </Col>
                  </Row>

                  {error && <Alert variant="danger" className="mt-3 mb-0" style={{ fontSize: '13px' }}>{error}</Alert>}

                  <Button type="submit" disabled={loading} className="w-100 mt-3"
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '16px', padding: '14px', color: colors.navy }}>
                    {loading ? <><Spinner size="sm" className="me-2" />Submitting...</> : 'Book My Free Demo'}
                  </Button>

                  <p style={{ color: '#999', fontSize: '12px', textAlign: 'center', marginTop: '12px', marginBottom: 0 }}>
                    Prefer to text? Send us a message at <a href="sms:5202519595" style={{ color: colors.gold }}>
                    (520) 251-9595</a> and our AI assistant Kira will help you get started.
                  </p>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Footer */}
      <div style={{ background: colors.navy2, padding: '24px 0', textAlign: 'center' }}>
        <p style={{ color: colors.muted, fontSize: '12px', margin: 0 }}>
          KairosIQ AI, Inc. | <a href="/events" style={{ color: colors.gold2, textDecoration: 'none' }}>Events</a> | <a href="/agent-portal/signup" style={{ color: colors.gold2, textDecoration: 'none' }}>Sign Up</a> | <a href="https://www.linkedin.com/company/112975801" style={{ color: colors.gold2, textDecoration: 'none' }}>LinkedIn</a>
        </p>
      </div>
    </div>
  );
};

export default RequestDemoPage;
